// ---------------------------------------------------------------------------
// MembershipPlans Renderer (editor key: MembershipPlans)
// ---------------------------------------------------------------------------
// Renders a grid of membership tiers parsed from a pipe-delimited textarea
// ("Name | Price | Features" per line, where features are a comma-separated
// list). Plan count is capped (1-6) and each plan's feature count to 12 so
// a typo cannot blow up the rendered grid. The middle plan in a 2+ tier
// list gets `--featured` for CSS hooks. The `ctaUrl` (optional) routes
// through the URL safelist; without a safe URL each card renders an inert
// `<button>` so the markup is keyboard reachable without fabricating a
// destination.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface MembershipPlansProps {
  heading?: string;
  plans?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

interface PlanEntry {
  name: string;
  price: string;
  features: string[];
}

const DEFAULT_CTA_LABEL = "Choose";
const MAX_PLANS = 6;
const MAX_FEATURES_PER_PLAN = 12;

function parsePlans(value: unknown): PlanEntry[] {
  if (typeof value !== "string") return [];
  const out: PlanEntry[] = [];
  for (const raw of value.split("\n")) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const parts = trimmed.split("|").map((part) => part.trim());
    const name = parts[0] ?? "";
    if (!name) continue;
    const price = parts[1] ?? "";
    const features = (parts[2] ?? "")
      .split(",")
      .map((feat) => feat.trim())
      .filter((feat) => feat.length > 0)
      .slice(0, MAX_FEATURES_PER_PLAN);
    out.push({ name, price, features });
    if (out.length >= MAX_PLANS) break;
  }
  return out;
}

function clampColumns(count: number): number {
  return Math.max(1, Math.min(3, count));
}

function renderPlan(
  plan: PlanEntry,
  index: number,
  total: number,
  ctaLabel: string,
  ctaUrl: string,
): string {
  const isFeatured = total >= 2 && index === Math.floor((total - 1) / 2);
  const featuredClass = isFeatured ? " ease-membership-plans__item--featured" : "";
  const priceHtml = plan.price
    ? `\n      <p class="ease-membership-plans__price">${text(plan.price)}</p>`
    : "";
  const featureItems = plan.features
    .map((feat) => `        <li class="ease-membership-plans__feature">${text(feat)}</li>`)
    .join("\n");
  const featuresHtml = plan.features.length > 0
    ? `\n      <ul class="ease-membership-plans__features" role="list">
${featureItems}
      </ul>`
    : "";
  const buttonLabel = `${ctaLabel} ${plan.name}`.trim();
  const sanitizedUrl = ctaUrl ? url(ctaUrl) : "";
  const hasSafeUrl = sanitizedUrl.length > 0 && sanitizedUrl !== "#";
  const cta = hasSafeUrl
    ? `<a class="ease-membership-plans__cta ease-btn ease-btn-primary" href="${sanitizedUrl}">${text(buttonLabel)}</a>`
    : `<button class="ease-membership-plans__cta ease-btn ease-btn-primary" type="button">${text(buttonLabel)}</button>`;

  return `    <article class="ease-membership-plans__item${featuredClass}" data-featured="${attr(isFeatured ? "yes" : "no")}">
      <h3 class="ease-membership-plans__name">${text(plan.name)}</h3>${priceHtml}${featuresHtml}
      ${cta}
    </article>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as MembershipPlansProps;

  const plans = parsePlans(p.plans);
  if (plans.length === 0) {
    return "<!-- membership-plans missing plans -->";
  }

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const ctaLabel = typeof p.ctaLabel === "string" && p.ctaLabel.trim()
    ? p.ctaLabel.trim()
    : DEFAULT_CTA_LABEL;
  const ctaUrl = typeof p.ctaUrl === "string" ? p.ctaUrl.trim() : "";
  const columns = clampColumns(plans.length);

  const headingHtml = heading
    ? `\n  <h2 class="ease-membership-plans__heading">${text(heading)}</h2>`
    : "";

  const cards = plans
    .map((plan, index) => renderPlan(plan, index, plans.length, ctaLabel, ctaUrl))
    .join("\n");

  return `<section class="ease-section ease-membership-plans ease-membership-plans--cols-${columns}" data-ease-columns="${attr(String(columns))}">${headingHtml}
  <div class="ease-membership-plans__grid" role="list">
${cards}
  </div>
</section>`;
}

registerRenderer("membership-plans", { render });
